<template>
  <v-dialog
    :model-value="modelValue"
    max-width="820px"
    persistent
    scrollable
    data-testid="cfgv2-import-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-deep-purple text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-import</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">นำเข้าเวอร์ชันคอนฟิกจากไฟล์</div>
          <div v-if="yearLabel" class="text-caption text-white-50">
            นำเข้าลงปีภาษี {{ yearLabel }}
          </div>
        </div>
        <v-btn
          icon
          variant="text"
          color="white"
          data-testid="cfgv2-import-close-btn"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="cfgv2-import-error"
        >
          {{ error }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          density="comfortable"
          class="mb-4"
          data-testid="cfgv2-import-hint"
        >
          ไฟล์นี้จะถูกสร้างเป็น <strong>เวอร์ชันคอนฟิกใหม่</strong> ใต้ปีนี้เสมอ และเป็น draft (ยังไม่ active) —
          หมวดหมู่ + FieldKey + ชุดสูตร + ชุดคำถามในไฟล์จะถูกสร้างตามลงไปทั้งชุด (ทุกตัวไม่ active) ·
          เวอร์ชันที่ใช้งานอยู่จะไม่ถูกแตะ
        </v-alert>

        <!-- ── 1. เลือกไฟล์ / วาง JSON ─────────────────────────────── -->
        <div class="d-flex align-center flex-wrap ga-2">
          <v-btn
            variant="outlined"
            size="small"
            prepend-icon="mdi-file-upload-outline"
            data-testid="cfgv2-import-pick"
            @click="pickImportFile"
          >
            เลือกไฟล์ .json
          </v-btn>
          <span v-if="fileName" class="text-caption text-medium-emphasis" data-testid="cfgv2-import-filename">
            {{ fileName }}
          </span>
        </div>
        <input
          ref="importFile"
          type="file"
          accept="application/json,.json"
          class="d-none"
          data-testid="cfgv2-import-file"
          @change="onImportFileChosen"
        >

        <v-textarea
          v-model="importText"
          class="mt-3"
          label="หรือวาง JSON ที่นี่"
          rows="6"
          variant="outlined"
          density="compact"
          hide-details
          data-testid="cfgv2-import-text"
          @update:model-value="onTextInput"
        />

        <v-alert
          v-if="parseError"
          type="error"
          variant="tonal"
          density="comfortable"
          class="mt-3"
          data-testid="cfgv2-import-parse-error"
        >
          {{ parseError }}
        </v-alert>

        <!-- ที่มาของไฟล์ (อ่านจากตัวไฟล์เอง — ยังไม่ได้ยิง dry-run) -->
        <v-alert
          v-else-if="sourceLines.length"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mt-3"
          icon="mdi-file-document-outline"
          data-testid="cfgv2-import-source"
        >
          <div class="text-subtitle-2 mb-1">ที่มาของไฟล์</div>
          <div v-for="line in sourceLines" :key="line.label" class="text-caption">
            {{ line.label }}: <strong>{{ line.value }}</strong>
          </div>
        </v-alert>

        <!-- provenance year ≠ the row's year: legitimate (moving a config to a new year) but
             worth a second look, so it is a warning and never a block -->
        <v-alert
          v-if="yearMismatch"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mt-3"
          data-testid="cfgv2-import-year-mismatch"
        >
          ไฟล์นี้ export จากปี <strong>{{ yearMismatch.fileYear }}</strong> แต่จะนำเข้าปี
          <strong>{{ yearMismatch.targetYear }}</strong> — ตรวจสอบว่าตั้งใจย้ายข้ามปีจริง
        </v-alert>

        <!-- ── 2. รหัสเวอร์ชัน / คำอธิบาย ────────────────────────────── -->
        <v-row class="mt-2" dense>
          <v-col cols="12" md="5">
            <v-text-field
              v-model="versionCode"
              label="รหัสเวอร์ชันใหม่"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="V1"
              hint="เว้นว่าง = ใช้รหัสในไฟล์ · ถ้าชนของเดิมในปีนี้ระบบจะต่อท้ายให้ (V1 → V1-2)"
              persistent-hint
              data-testid="cfgv2-import-code-input"
              @update:model-value="onCodeInput"
            />
          </v-col>
          <v-col cols="12" md="7">
            <v-text-field
              v-model="description"
              label="คำอธิบาย"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="รายละเอียดเวอร์ชันนี้"
              hint="เว้นว่าง = ใช้คำอธิบายในไฟล์"
              persistent-hint
              data-testid="cfgv2-import-description-input"
              @update:model-value="onDescriptionInput"
            />
          </v-col>
        </v-row>

        <!-- ── 3. ผลการตรวจไฟล์ (dry-run) ───────────────────────────── -->
        <v-card
          v-if="preview"
          variant="tonal"
          class="mt-4"
          data-testid="cfgv2-import-preview"
        >
          <v-card-text class="pa-4">
            <div class="text-subtitle-2 mb-2">ผลการตรวจไฟล์</div>

            <div class="d-flex align-center flex-wrap ga-2 mb-3">
              <v-chip size="small" variant="flat" color="deep-purple" prepend-icon="mdi-tag">
                รหัสเวอร์ชันที่จะได้: {{ preview.versionCode }}
              </v-chip>
              <v-chip
                v-if="preview.versionCodeAdjusted"
                size="small"
                variant="tonal"
                color="warning"
                prepend-icon="mdi-pencil-outline"
                data-testid="cfgv2-import-code-adjusted"
              >
                ถูกปรับ
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-shape-outline">
                {{ preview.categoryCount || 0 }} หมวดหมู่
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-key-outline">
                {{ preview.ruleCount || 0 }} FieldKey
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-function-variant">
                {{ previewCalcRows.length }} ชุดสูตร
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-comment-question-outline">
                {{ previewQuestionRows.length }} ชุดคำถาม
              </v-chip>
            </div>

            <v-alert
              v-if="preview.versionCodeAdjusted"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              รหัสที่ระบุชนกับเวอร์ชันเดิมในปีนี้ — ระบบเปลี่ยนให้เป็น
              <strong>{{ preview.versionCode }}</strong> อัตโนมัติ
            </v-alert>

            <!-- 🔴 the one number that means "silently broken on arrival" — loud, red, first -->
            <v-alert
              v-if="(preview.totalDroppedCalcConfigCount || 0) > 0"
              type="error"
              variant="flat"
              density="comfortable"
              class="mb-3"
              icon="mdi-calculator-variant-outline"
              data-testid="cfgv2-import-dropped-calc"
            >
              <div class="text-subtitle-2">
                ข้อ CALCULATE {{ preview.totalDroppedCalcConfigCount }} ข้อจะไม่คำนวณ
                เพราะไม่มีชุดสูตรที่อ้างถึงในไฟล์
              </div>
              <div class="text-caption">
                ข้อเหล่านั้นจะถูกสร้างโดยไม่ผูกสูตร (calcConfig ว่าง) — ถ้าต้องการให้คำนวณได้
                ให้ส่งออกใหม่โดยติ๊กชุดสูตรที่ชุดคำถามอ้างถึงมาด้วย
              </div>
            </v-alert>

            <table v-if="previewCalcRows.length" class="cfgv2-import-table mb-3" data-testid="cfgv2-import-preview-calc">
              <thead>
                <tr>
                  <th>ชุดสูตรในไฟล์</th>
                  <th>รหัสที่จะได้</th>
                  <th class="num">สูตร</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in previewCalcRows" :key="`pc-${i}`">
                  <td class="code">{{ row.sourceCode || '—' }}</td>
                  <td class="code">
                    {{ row.versionCode || row.sourceCode || '—' }}
                    <v-chip v-if="row.versionCodeAdjusted" size="x-small" color="warning" variant="tonal" class="ml-1">ถูกปรับ</v-chip>
                  </td>
                  <td class="num">{{ row.functionCount ?? '—' }}</td>
                </tr>
              </tbody>
            </table>

            <table v-if="previewQuestionRows.length" class="cfgv2-import-table mb-3" data-testid="cfgv2-import-preview-question">
              <thead>
                <tr>
                  <th>ชุดคำถามในไฟล์</th>
                  <th>รหัสที่จะได้</th>
                  <th class="num">ขั้นตอน</th>
                  <th class="num">ข้อ</th>
                  <th class="num">CALCULATE ที่ไม่คำนวณ</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in previewQuestionRows" :key="`pq-${i}`">
                  <td class="code">{{ row.sourceCode || '—' }}</td>
                  <td class="code">
                    {{ row.versionCode || row.sourceCode || '—' }}
                    <v-chip v-if="row.versionCodeAdjusted" size="x-small" color="warning" variant="tonal" class="ml-1">ถูกปรับ</v-chip>
                  </td>
                  <td class="num">{{ row.stepCount ?? '—' }}</td>
                  <td class="num">{{ row.itemCount ?? '—' }}</td>
                  <td class="num" :class="{ 'text-error font-weight-bold': (row.droppedCalcConfigCount || 0) > 0 }">
                    {{ row.droppedCalcConfigCount ?? '—' }}
                  </td>
                </tr>
              </tbody>
            </table>

            <v-alert
              v-if="previewWarnings.length"
              type="warning"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-import-warnings"
            >
              <div class="text-subtitle-2 mb-1">
                พบข้อควรระวัง {{ previewWarnings.length }} รายการ — นำเข้าต่อได้ แต่ควรอ่านก่อน
              </div>
              <ul class="cfgv2-import-warn-list">
                <li v-for="(w, i) in previewWarnings" :key="i" class="text-caption">{{ w }}</li>
              </ul>
            </v-alert>
            <v-alert
              v-else-if="(preview.totalDroppedCalcConfigCount || 0) === 0"
              type="success"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-import-ok"
            >
              ตรวจไฟล์แล้ว ไม่พบปัญหา — นำเข้าได้เลย
            </v-alert>

            <div class="text-caption text-medium-emphasis mt-2" data-testid="cfgv2-import-note">
              การตรวจไฟล์ไม่ได้คอมไพล์สูตร — ผล compile จะรู้ตอนนำเข้าจริง
            </div>
          </v-card-text>
        </v-card>

        <!-- ── 4. ผลการนำเข้าจริง ───────────────────────────────────── -->
        <!-- The parent normally closes this dialog as soon as `imported` fires, so this block is
             a belt-and-braces copy of what the page's snackbar says: a partial load must never be
             something the user has to guess at. -->
        <v-card
          v-if="importResult"
          variant="tonal"
          class="mt-4"
          data-testid="cfgv2-import-result"
        >
          <v-card-text class="pa-4">
            <div class="text-subtitle-2 mb-2">ผลการนำเข้า</div>
            <div class="d-flex align-center flex-wrap ga-2 mb-3">
              <v-chip size="small" variant="flat" color="success" prepend-icon="mdi-check">
                นำเข้าเป็นเวอร์ชัน {{ importResult.versionCode }}
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-shape-outline">
                {{ importResult.categoryCount || 0 }} หมวดหมู่
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-key-outline">
                {{ importResult.ruleCount || 0 }} FieldKey
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-function-variant">
                {{ resultCalcRows.length }} ชุดสูตร
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-comment-question-outline">
                {{ resultQuestionRows.length }} ชุดคำถาม
              </v-chip>
              <v-chip
                v-if="(importResult.totalCompileErrorCount || 0) > 0"
                size="small"
                variant="tonal"
                color="error"
                prepend-icon="mdi-alert-circle-outline"
                data-testid="cfgv2-import-result-compile-errors"
              >
                คอมไพล์ไม่ผ่าน {{ importResult.totalCompileErrorCount }} สูตร
              </v-chip>
            </div>

            <v-alert
              v-if="(importResult.totalDroppedCalcConfigCount || 0) > 0"
              type="error"
              variant="flat"
              density="comfortable"
              class="mb-3"
              icon="mdi-calculator-variant-outline"
              data-testid="cfgv2-import-result-dropped-calc"
            >
              ข้อ CALCULATE {{ importResult.totalDroppedCalcConfigCount }} ข้อถูกสร้างโดยไม่ผูกสูตร
              และจะไม่คำนวณ
            </v-alert>

            <v-alert
              v-if="resultWarnings.length"
              type="warning"
              variant="tonal"
              density="comfortable"
              data-testid="cfgv2-import-result-warnings"
            >
              <div class="text-subtitle-2 mb-1">
                นำเข้าแล้ว แต่มีคำเตือน {{ resultWarnings.length }} รายการ
              </div>
              <ul class="cfgv2-import-warn-list">
                <li v-for="(w, i) in resultWarnings" :key="i" class="text-caption">{{ w }}</li>
              </ul>
            </v-alert>
          </v-card-text>
        </v-card>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="outlined"
          :disabled="checking || importing"
          data-testid="cfgv2-import-cancel-btn"
          @click="close"
        >
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="deep-purple"
          variant="tonal"
          :disabled="!parsedBundle || importing"
          :loading="checking"
          data-testid="cfgv2-import-check-btn"
          @click="runDryRun"
        >
          <v-icon start>mdi-file-search-outline</v-icon>
          ตรวจไฟล์
        </v-btn>
        <v-btn
          color="deep-purple"
          variant="elevated"
          :disabled="!preview || checking"
          :loading="importing"
          data-testid="cfgv2-import-submit-btn"
          @click="runImport"
        >
          <v-icon start>mdi-import</v-icon>
          นำเข้าเลย
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Import a Tax Config V2 bundle (`.json` produced by the config-version row's «ส่งออกเป็นไฟล์»)
 * as a NEW config version under ONE tax year — the config-level sibling of
 * `components/TaxCalcImportV2Dialog.vue` / `TaxQuestionImportV2Dialog.vue`, deliberately the
 * same shape so every «นำเข้า» button on the config tree behaves identically.
 *
 * Contract with the parent (`views/TaxConfigurationV2.vue`):
 *   - the parent owns the target identity (`yearId` — the year row whose «นำเข้า» button was
 *     pressed) and the snackbar;
 *   - this dialog reports its own errors inline (`v-alert`), never through a snackbar of its own;
 *   - `imported` fires AFTER the real (non-dry-run) call succeeded, carrying the whole
 *     `ImportTaxConfigBundleV2Result`, and this dialog does NOT close itself — the parent
 *     decides, because it is the one that must reload the year's versions and surface the
 *     warnings. «ยกเลิก» / «X» still emit `update:modelValue: false` normally.
 *
 * Two-step by design: «ตรวจไฟล์» calls the import with `dryRun: true` — an IN-FILE cross-check
 * (FieldKeys vs `rules[]`, question `calcConfig` vs `calcVersions[]`, code collision) plus
 * counts. It writes nothing, calls no child import and compiles nothing; its result is what
 * unlocks «นำเข้าเลย». Editing anything afterwards — the pasted JSON, the code, the description —
 * throws that preview away, because a preview that no longer describes the text on screen is
 * worse than no preview at all.
 *
 * `totalDroppedCalcConfigCount > 0` is THE number to shout about: those CALCULATE items are
 * created with an empty calcConfig and never compute on this machine (G5). It is shown in red
 * on both the preview and the real result. Compile errors only exist on the real run.
 *
 * `yearId` is ALWAYS the target — `bundle.source.*` is untrusted display-only provenance and
 * never resolves anything (A1); a `source.taxYearCode` that differs from the row's year is a
 * yellow warning, not a redirect.
 */
import { taxConfigV2Api } from '@/services/taxConfigV2Api';

/** Envelope marker written by the export endpoint — sniffed before anything else is touched. */
const BUNDLE_KIND = 'lhb.taxconfig.v2.bundle';

export default {
  name: 'TaxConfigImportV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** target tax year — the new config version is created under THIS one */
    yearId: { type: Number, default: null },
    /**
     * the target year's `taxYearCode` (e.g. `2568`) — shown as the subtitle AND compared with
     * the file's `source.taxYearCode` for the cross-year warning
     */
    yearLabel: { type: String, default: '' },
    /** parent's suggestion for the new code, used until the file supplies its own */
    suggestedCode: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'imported'],
  data() {
    return {
      importText: '',
      fileName: '',
      /** why the pasted/loaded text is not a usable bundle (set from `parsedBundle`) */
      parseError: '',
      /** API-level failure of the dry run / the import itself */
      error: '',
      /** `ImportTaxConfigBundleV2Result` of the last dry run — gates «นำเข้าเลย» */
      preview: null,
      /** `ImportTaxConfigBundleV2Result` of the REAL import — kept so warnings survive on screen */
      importResult: null,
      versionCode: '',
      description: '',
      /** stop the auto-prefill from the file once the user has typed their own */
      codeTouched: false,
      descriptionTouched: false,
      checking: false,
      importing: false,
    };
  },
  computed: {
    /**
     * The parsed bundle, or null while the text does not hold a usable one. Doubles as the
     * «ตรวจไฟล์» button's enable test — parsing here rather than on submit means a wrong file
     * (a calc bundle, a question bundle, a V1 `lhb.taxconfig.bundle`, a Playground snapshot) is
     * caught before the server is called. Sets `parseError` as a side effect so the reason is
     * visible.
     */
    parsedBundle() {
      const raw = (this.importText || '').trim();
      if (!raw) { this.parseError = ''; return null; }
      let bundle;
      try {
        bundle = JSON.parse(raw);
      } catch (e) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ JSON ที่อ่านได้';
        return null;
      }
      if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ไฟล์เวอร์ชันคอนฟิก Tax Config V2';
        return null;
      }
      const kind = String(bundle.kind || '').trim().toLowerCase();
      if (kind !== BUNDLE_KIND) {
        this.parseError = bundle.kind
          ? `ไฟล์นี้ไม่ใช่ไฟล์เวอร์ชันคอนฟิก Tax Config V2 — ไฟล์นี้เป็น "${bundle.kind}"`
          : 'ไฟล์นี้ไม่ใช่ไฟล์เวอร์ชันคอนฟิก Tax Config V2 — ไม่พบ kind ในไฟล์';
        return null;
      }
      if (!Array.isArray(bundle.rules)) {
        this.parseError = 'ไฟล์นี้ไม่มี registry ของ FieldKey (rules) — ไฟล์อาจเสียหาย';
        return null;
      }
      this.parseError = '';
      return bundle;
    },
    /** `source.*` of the loaded file — every null field is DROPPED, never rendered as "null". */
    sourceLines() {
      const b = this.parsedBundle;
      if (!b) return [];
      const src = b.source || {};
      const lines = [];
      const push = (label, value) => {
        if (value === null || value === undefined || String(value).trim() === '') return;
        lines.push({ label, value: String(value) });
      };
      push('ปีภาษีต้นทาง', src.taxYearCode);
      push('ปี พ.ศ.', src.beYear);
      push('เวอร์ชันต้นทาง', src.configVersionCode);
      push('คำอธิบาย', src.description);
      const count = (arr) => (Array.isArray(arr) ? arr.length : 0);
      lines.push({
        label: 'ในไฟล์',
        value: `${count(b.categories)} หมวดหมู่ · ${count(b.rules)} FieldKey · `
          + `${count(b.calcVersions)} ชุดสูตร · ${count(b.questionVersions)} ชุดคำถาม`,
      });
      if (b.exportedAt) push('ส่งออกเมื่อ', b.exportedAt);
      if (b.exportedBy) push('ส่งออกโดย', b.exportedBy);
      return lines;
    },
    /** file's provenance year vs the row's year — null when equal or when either side is unknown */
    yearMismatch() {
      const b = this.parsedBundle;
      const fileYear = b && b.source ? b.source.taxYearCode : null;
      const norm = (v) => String(v ?? '').trim().toUpperCase();
      if (!norm(fileYear) || !norm(this.yearLabel)) return null;
      if (norm(fileYear) === norm(this.yearLabel)) return null;
      return { fileYear: String(fileYear), targetYear: this.yearLabel };
    },
    previewWarnings() {
      const w = this.preview && this.preview.warnings;
      return Array.isArray(w) ? w : [];
    },
    resultWarnings() {
      const w = this.importResult && this.importResult.warnings;
      return Array.isArray(w) ? w : [];
    },
    /**
     * Child rows of the dry run. The server fills `calcVersions[]` / `questionVersions[]` with
     * in-file counts; if a row set comes back empty while the file does hold children, fall
     * back to counting the file itself so the panel never shows "0 ชุดสูตร" for a file that
     * clearly has some.
     */
    previewCalcRows() {
      const rows = this.preview && this.preview.calcVersions;
      if (Array.isArray(rows) && rows.length) return rows;
      return this.fileCalcRows();
    },
    previewQuestionRows() {
      const rows = this.preview && this.preview.questionVersions;
      if (Array.isArray(rows) && rows.length) return rows;
      return this.fileQuestionRows();
    },
    resultCalcRows() {
      const rows = this.importResult && this.importResult.calcVersions;
      return Array.isArray(rows) ? rows : [];
    },
    resultQuestionRows() {
      const rows = this.importResult && this.importResult.questionVersions;
      return Array.isArray(rows) ? rows : [];
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetState();
    },
    /**
     * Prefill the code/description from the file, but never over something the user typed.
     * The file's own header (`configVersion.versionCode`) wins over the parent's suggestion —
     * that is the code the source machine used, which is what a mover expects to see again.
     */
    parsedBundle(bundle) {
      const cfg = (bundle && bundle.configVersion) || {};
      const src = (bundle && bundle.source) || {};
      if (!this.codeTouched) {
        this.versionCode = this.normalizeCode(
          cfg.versionCode || src.configVersionCode || this.suggestedCode || '',
        );
      }
      if (!this.descriptionTouched) {
        this.description = cfg.description || src.description || '';
      }
    },
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    /** fallback rows read straight from the file (see `previewCalcRows`) */
    fileCalcRows() {
      const b = this.parsedBundle;
      const list = b && Array.isArray(b.calcVersions) ? b.calcVersions : [];
      return list.map((child) => ({
        sourceCode: child && child.source ? child.source.calcVersionCode : null,
        versionCode: null,
        versionCodeAdjusted: false,
        functionCount: child && Array.isArray(child.functions) ? child.functions.length : null,
      }));
    },
    fileQuestionRows() {
      const b = this.parsedBundle;
      const list = b && Array.isArray(b.questionVersions) ? b.questionVersions : [];
      return list.map((child) => ({
        sourceCode: child && child.source ? child.source.questionVersionCode : null,
        versionCode: null,
        versionCodeAdjusted: false,
        stepCount: child && Array.isArray(child.steps) ? child.steps.length : null,
        itemCount: child && Array.isArray(child.items) ? child.items.length : null,
        droppedCalcConfigCount: null,
      }));
    },
    resetState() {
      this.importText = '';
      this.fileName = '';
      this.parseError = '';
      this.error = '';
      this.preview = null;
      this.importResult = null;
      this.versionCode = this.normalizeCode(this.suggestedCode);
      this.description = '';
      this.codeTouched = false;
      this.descriptionTouched = false;
      this.checking = false;
      this.importing = false;
      const el = this.$refs.importFile;
      if (el) el.value = '';
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    pickImportFile() {
      const el = this.$refs.importFile;
      // Clearing FIRST so re-picking the SAME file still fires `change` (the browser skips the
      // event when the value is unchanged).
      if (el) { el.value = ''; el.click(); }
    },
    onImportFileChosen(evt) {
      const file = evt && evt.target && evt.target.files && evt.target.files[0];
      if (!file) return;
      this.fileName = file.name || '';
      this.error = '';
      this.preview = null;
      this.importResult = null;
      const reader = new FileReader();
      reader.onload = () => { this.importText = String(reader.result || ''); };
      reader.onerror = () => {
        this.importText = '';
        this.parseError = 'อ่านไฟล์ไม่สำเร็จ';
      };
      reader.readAsText(file);
    },
    /** Any edit invalidates the dry run — the preview must describe the text that is there NOW. */
    onTextInput() {
      this.preview = null;
      this.importResult = null;
      this.error = '';
    },
    onCodeInput(value) {
      this.codeTouched = true;
      this.versionCode = this.normalizeCode(value);
      // The code is part of what the dry run decided (collision → `V1-2`), so re-check is required.
      this.preview = null;
    },
    onDescriptionInput() {
      this.descriptionTouched = true;
      // The description travels with the same request the dry run validated — re-check it too.
      this.preview = null;
    },
    /** Shared args of both calls — the only difference between them is `dryRun`. */
    buildRequest(dryRun) {
      return {
        bundle: this.parsedBundle,
        // 🔴 trailing capital `ID` — the service forwards this key verbatim to ASP.NET.
        yearID: this.yearId,
        versionCode: this.normalizeCode(this.versionCode) || null,
        description: (this.description || '').trim() || null,
        dryRun,
      };
    },
    async runDryRun() {
      if (!this.parsedBundle || this.checking || this.importing) return;
      if (!(Number(this.yearId) > 0)) {
        this.error = 'ไม่รู้ว่าจะนำเข้าลงปีไหน — ปิดหน้าต่างแล้วกดปุ่มนำเข้าใหม่อีกครั้ง';
        return;
      }
      this.checking = true;
      this.error = '';
      this.preview = null;
      this.importResult = null;
      try {
        this.preview = await taxConfigV2Api.importBundle(this.buildRequest(true));
      } catch (e) {
        this.error = (e && e.message) || 'ตรวจไฟล์ไม่สำเร็จ';
      } finally {
        this.checking = false;
      }
    },
    async runImport() {
      if (!this.preview || this.checking || this.importing) return;
      this.importing = true;
      this.error = '';
      try {
        const result = await taxConfigV2Api.importBundle(this.buildRequest(false));
        this.importResult = result;
        // Deliberately NOT closing here — the parent reloads the year's versions, shows the
        // warnings and decides when this dialog goes away.
        this.$emit('imported', result);
      } catch (e) {
        this.error = (e && e.message) || 'นำเข้าเวอร์ชันคอนฟิกไม่สำเร็จ';
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
.text-white-50 {
  opacity: 0.8;
}
.cfgv2-import-warn-list {
  margin: 0;
  padding-left: 18px;
}
.cfgv2-import-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}
.cfgv2-import-table th,
.cfgv2-import-table td {
  padding: 4px 8px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  text-align: left;
  vertical-align: middle;
}
.cfgv2-import-table th {
  font-weight: 600;
  opacity: 0.8;
}
.cfgv2-import-table .num {
  text-align: right;
  white-space: nowrap;
}
.cfgv2-import-table .code {
  font-family: 'Roboto Mono', 'SFMono-Regular', Consolas, monospace;
}
</style>
